import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';
import _ from 'lodash';

import { useInfiniteQuery } from '@tanstack/react-query';

import { ClientOptions, default as Client } from 'models/Client';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const clientsQueryKeys = {
  index: ['clients'] as const,
};

type IndexParams = BaseIndexQueryParams & {
  leaf?: boolean;
  // ClientPolicy::Scope's modes, mirroring the group ones: `network` is the clients owning a group in the
  // user's network.
  accessModes?: ('allowed' | 'network' | 'manager_network')[];
  // With a mode, takes the view-only path through the groups, as the buildings picker does.
  viewOnly?: boolean;
  usesCentralIntake?: boolean;
  // The clients with an active provider group of this type that `clientId` could refer to. With a picking location, each
  // carries `hasPreferredGroups`: whether one of those groups is on its preferred list.
  networkProviderType?: { clientId: string; type: string; preferredForGroupId?: string };
};

export async function indexClients(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<Paginated<ClientOptions>>(`clients`, { params, signal }).then((res) => {
    return {
      ...res.data,
      data: res.data.data.map((x) => new Client(x)),
    };
  });
}

type ForResourceParams = {
  id: string;
  type: string;
};
export async function forResourceClients(requestParams: ForResourceParams, signal: GenericAbortSignal) {
  const params = { resourceId: requestParams.id, resourceType: requestParams.type, viewOnly: true };

  return http.get<Paginated<ClientOptions>>(`clients/for_resource`, { params, signal }).then((res) => {
    return {
      ...res.data,
      data: res.data.data.map((x) => new Client(x)),
    };
  });
}

export const useInfiniteClientOptions = (params: IndexParams) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: [...clientsQueryKeys.index, { ...params, search }],
    queryFn: ({ signal, pageParam }) => indexClients({ ...params, page: pageParam, search }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
